import React from 'react'
import { Link } from 'react-router-dom'
import {
  resolveAdventureStatus,
  resolveAdventureMissionType,
  resolveNarrativeTime,
  narrativeTimeLabel,
} from '../../features/adventure/shared/adventureConstants'
import './FeaturedAdventureCard.css'

const MISSION_SYMBOLS = {
  Combate: '⚔',
  Espionaje: '◈',
  Rescate: '◇',
  Exploración: '◉',
  Comercio: '◆',
  Diplomacia: '✦',
  Misión: '⚔',
  General: '◈',
}

const DIFFICULTY_CONFIG = {
  Social: { label: 'Social', cls: 'difficulty--social' },
  Principiante: { label: 'Principiante', cls: 'difficulty--beginner' },
  Intermedio: { label: 'Veterano', cls: 'difficulty--intermediate' },
  Avanzado: { label: 'Élite', cls: 'difficulty--advanced' },
  Extremo: { label: 'Legendario', cls: 'difficulty--extreme' },
}

const STATUS_CONFIG = {
  Abierta: { symbol: '●', cls: 'fadv-status--open', label: 'Abierta' },
  'En progreso': { symbol: '◆', cls: 'fadv-status--progress', label: 'En curso' },
  Completada: { symbol: '◇', cls: 'fadv-status--completed', label: 'Cerrada' },
  Cancelada: { symbol: '✕', cls: 'fadv-status--cancelled', label: 'Cancelada' },
}

function extractPreview(content, previewText, maxLen = 1000) {
  let text = previewText || content || 'Sin descripción disponible.'
  if (!previewText && content) {
    text = content
      .replace(/<[^>]*>/g, '')
      .replace(/\[([^\]]*)\]/g, '')
      .trim()
  }
  if (Number.isFinite(maxLen) && maxLen > 0 && text.length > maxLen) {
    text = `${text.substring(0, maxLen).trim()}…`
  }
  return text.charAt(0).toUpperCase() + text.slice(1)
}

export default function FeaturedAdventureCard({ adventure }) {
  if (!adventure) return null

  const mission = resolveAdventureMissionType(adventure)
  const missionSymbol = MISSION_SYMBOLS[mission] || '◈'
  const diffCfg = DIFFICULTY_CONFIG[adventure.difficulty] || DIFFICULTY_CONFIG['Principiante']
  const statusCfg = STATUS_CONFIG[resolveAdventureStatus(adventure)] || STATUS_CONFIG['Abierta']
  const timeKey = resolveNarrativeTime(adventure)
  const timeLabel = narrativeTimeLabel(timeKey)
  const participants = Number(adventure.current_participants || 0)
  const turnNum = Number(adventure.current_turn)
  const turnMax = Number(adventure.max_turns)
  const hasTurns = Number.isFinite(turnNum) && turnNum > 0
  const turnLabel = hasTurns
    ? Number.isFinite(turnMax) && turnMax > 0
      ? `Turno ${turnNum}/${turnMax}`
      : `Turno ${turnNum}`
    : null
  const preview = extractPreview(adventure.content, adventure.preview_text)

  return (
    <article
      className={`fadv${
        statusCfg.cls.includes('completed')
          ? ' fadv--closed'
          : statusCfg.cls.includes('cancelled')
            ? ' fadv--cancelled'
            : statusCfg.cls.includes('progress')
              ? ' fadv--in-progress'
              : ''
      }`}
      aria-label="Aventura destacada"
    >
      <aside className="fadv__seal" aria-hidden="true">
        <span className="fadv__seal-mark">{missionSymbol}</span>
        <span className="fadv__seal-label">Destacada</span>
      </aside>

      <div className="fadv__main">
        <header className="fadv__kicker">
          <span className="fadv__mission-type">{mission.toUpperCase()}</span>
          <span className={`fadv__difficulty ${diffCfg.cls}`}>{diffCfg.label}</span>
          <span className={`fadv__time fadv__time--${timeKey}`} title={`Tiempo: ${timeLabel}`}>
            {timeLabel}
          </span>
          <span className={`fadv__status ${statusCfg.cls}`} title={`Estado: ${statusCfg.label}`}>
            <span className="fadv__status-mark" aria-hidden="true">
              {statusCfg.symbol}
            </span>
            <span className="fadv__status-label">{statusCfg.label}</span>
          </span>
        </header>

        <Link to={`/aventura/${adventure.slug}`} className="fadv__title-link">
          <h3 className="fadv__title">{adventure.title}</h3>
        </Link>

        <div className="fadv__meta">
          <span>
            Crónica de{' '}
            <Link to={`/personajes/${adventure.charSlug}`} className="fadv__author-link">
              {adventure.author || 'un viajero sin nombre'}
            </Link>
          </span>
          <span className="fadv__meta-sep" aria-hidden="true">
            ·
          </span>
          {turnLabel && (
            <>
              <span className="fadv__meta-item fadv__meta-item--turn">{turnLabel}</span>
              <span className="fadv__meta-sep" aria-hidden="true">
                ·
              </span>
            </>
          )}
          <span className="fadv__meta-item">
            {participants} {participants === 1 ? 'participante' : 'participantes'}
          </span>
          <span className="fadv__meta-sep" aria-hidden="true">
            ·
          </span>
          <span className="fadv__meta-item">{adventure.replies_count || 0} resp.</span>
          <span className="fadv__meta-sep" aria-hidden="true">
            ·
          </span>
          <span className="fadv__meta-item">{adventure.views || 0} vistas</span>
        </div>

        <blockquote className="fadv__extract">{preview}</blockquote>

        <footer className="fadv__actions">
          <Link to={`/aventura/${adventure.slug}`} className="fadv__cta">
            Abrir crónica
            <span className="fadv__cta-arrow" aria-hidden="true">
              →
            </span>
          </Link>
        </footer>
      </div>
    </article>
  )
}
